import { isAdaAssetId } from "@sundae/cardano-utils";
import { IMappedAsset } from "@sundae/wallet-react";
import { AssetAmount } from "@sundaeswap/asset";
import { getTokensForLp } from "@sundaeswap/cpp";
import { Fraction } from "@sundaeswap/fraction";
import orderBy from "lodash/orderBy";
import round from "lodash/round";

import { ADA_METADATA } from "../constants/cardano.constants";
import {
  Asset,
  FreezerItem,
  FreezerReward,
  PoolFragmentFragment,
} from "../gql/generated/stats2.sdk";
import {
  CalculationStats,
  Delegation,
  Earning,
  EarningStatus,
  Pool,
  Position,
  Program,
  Value,
} from "../gql/generated/yieldFarmingV2.sdk";
import { useYieldFarmingProgramsEstimation } from "../gql/hooks/farms.query";
import { IAssetMetaData } from "../types/Asset.types";
import {
  IEnhancedDelegation,
  IEnhancedMergedPosition,
  IEnhancedValue,
  IMergedPosition,
  IYieldFarmingTotalEarnings,
  TGroupedDelegation,
} from "../types/Farm.types";
import { TAssetIdPoolMap, calculateAssetValueInADAAndUSD, isLPAsset } from "./pool.utils";

/**
 * Calculates the percentage of tokens that are locked in an asset.
 *
 * This function takes an asset and a locking amount. It calculates the percentage of
 * the asset's total quantity that is not locked by subtracting the locking amount from
 * the asset's quantity and dividing by the asset's total quantity. It then subtracts
 * this percentage from 100 to get the percentage of locked tokens. The result is rounded
 * to the nearest whole number.
 *
 * If the asset is not defined, the function returns 0.
 *
 * @param {IMappedAsset<IAssetMetaData>} asset - The asset object containing metadata and quantity.
 * @param {number} lockingAmount - The amount of tokens that are locked.
 *
 * @returns {number} The percentage of tokens that are locked in the asset. The result is rounded
 * to the nearest whole number according to the asset's decimals.
 */
export const calculatePercentageOfLockedTokens = (
  asset: IMappedAsset<IAssetMetaData> | undefined,
  lockingAmount: number
) => {
  if (!asset) return 0;

  const decreasedValue = asset.quantity.value.subtract(lockingAmount);
  const delta = decreasedValue.divide(asset.quantity.value).multiply(Fraction.HUNDRED);

  return round(Fraction.HUNDRED.subtract(delta).toNumber(), asset.metadata.decimals);
};

/**
 * Group rewards by assets from a list of farms.
 *
 * This function takes an array of farms and reduces it into an array of all rewards.
 * It then groups these rewards by the asset ID and sums the quantity for each distinct asset ID.
 * It returns an array of objects, each containing the asset metadata and the total quantity of that asset.
 *
 * @param {FreezerItem[]} farms - An array of farms where each farm is an object that includes rewards.
 *
 * @returns {Array<{asset: Asset, quantity: string}>} An array of reward objects each including the asset
 * metadata and the total quantity of that asset as a string.
 */
export const groupRewards = (farms: FreezerItem[]) => {
  const allRewards = farms.reduce((arr: FreezerReward[], { rewards }) => [...arr, ...rewards], []);
  const rewardsObj = allRewards.reduce(
    (obj: { [key: string]: { asset: Asset; quantity: number } }, reward) => {
      if (!reward.asset) return obj;
      const key = reward.asset?.assetId;
      if (obj[key]) {
        obj[key] = { asset: reward.asset, quantity: obj[key].quantity + Number(reward.quantity) };
      } else {
        obj[key] = { asset: reward.asset, quantity: Number(reward.quantity) };
      }
      return obj;
    },
    {}
  );
  return Object.values(rewardsObj).map((reward) => ({
    ...reward,
    quantity: String(reward.quantity),
  }));
};

/**
 * This function calculates and returns the quantity of an asset from a farm.
 *
 * It accepts the quantity of an asset and a farm object as arguments.
 * The function multiplies the provided asset quantity with the quantity of the farm,
 * then divides the result by the liquidity pool's quantity.
 *
 * All quantities are treated as BigInts, ensuring accuracy with large numbers.
 *
 * @param {string} assetQuantity - The quantity of an asset as a string.
 * @param {FreezerItem} farm - The farm object. It should have a 'quantity' property
 *                             representing the quantity of the farm, and a 'pool' property
 *                             with a 'quantityLP' property representing the liquidity pool's quantity.
 *
 * @returns {BigInt} The calculated asset quantity as a BigInt.
 */
export const calculateAssetQuantityFromFarm = (
  farm: FreezerItem,
  assetQuantity?: string | null
) => {
  const { quantityLP } = farm.pool || {};
  if (!quantityLP || !assetQuantity) return 0n;
  return (BigInt(assetQuantity) * BigInt(farm.quantity)) / BigInt(quantityLP);
};

/**
 * This function takes a pool and an array of farms, and returns the total asset balance
 * from the provided farms.
 *
 * The function starts with an initial value which contains the pool's assets metadata
 * and initial quantity of 0 for both assets. If no farms are provided, this initial value
 * is returned.
 *
 * If farms are provided, the function reduces over the farms. For each farm, it calculates
 * the asset quantities using the `calculateAssetQuantityFromFarm` function (not included in this description).
 * It then returns a new object with the assets metadata and updated quantities. The new
 * quantities are calculated by adding the asset quantity of the current farm to the total
 * quantity calculated so far.
 *
 * @param {PoolFragmentFragment} pool - The pool object containing the metadata of the assets.
 * @param {FreezerItem[]} farms - An array of farms. Each farm object should have properties
 *                                'pool' with properties 'quantityA' and 'quantityB',
 *                                representing the quantities of each asset.
 *
 * @returns {Object} An object containing the metadata and total quantities of assetA and assetB.
 *                   The quantities are calculated as the sum of the quantities from each farm,
 *                   and are in BigInt format.
 */
export const getTotalAssetBalanceFromFarms = (pool: PoolFragmentFragment, farms: FreezerItem[]) => {
  const initialValue = {
    assetA: { metadata: pool.assetA, quantity: 0n },
    assetB: { metadata: pool.assetB, quantity: 0n },
  };
  if (!farms.length) return initialValue;
  return farms.reduce((obj, farm) => {
    const assetAQuantity = calculateAssetQuantityFromFarm(farm, farm.pool?.quantityA);
    const assetBQuantity = calculateAssetQuantityFromFarm(farm, farm.pool?.quantityB);
    return {
      assetA: { ...obj.assetA, quantity: obj.assetA.quantity + assetAQuantity },
      assetB: { ...obj.assetB, quantity: obj.assetB.quantity + assetBQuantity },
    };
  }, initialValue);
};

/**
 * Filters and sorts a list of farms associated with a specific pool based on their next reward dates.
 *
 * This function filters the given list of farms by matching the pool's `assetID` with the `assetID`
 * of each farm's pool. It then sorts the filtered farms in ascending order by their `nextRewardAt` dates.
 *
 * @param {FreezerItem[]} farms - The list of farms to filter and sort.
 * @param {PoolFragmentFragment} pool - The pool used to filter the farms. Only farms associated with this pool are returned.
 *
 * @returns {FreezerItem[]} The filtered and sorted list of farms. Each farm in this list is associated with the given pool,
 * and the list is sorted in ascending order by the `nextRewardAt` dates of the farms.
 */
export const filterAndSortFarmsByPoolAndRewardDate = (
  farms: FreezerItem[] | undefined,
  pool: PoolFragmentFragment
) => {
  return orderBy(farms?.filter((farm) => farm.pool?.assetID === pool.assetID) ?? [], [
    ({ nextRewardAt }) => nextRewardAt && new Date(nextRewardAt.format),
  ]);
};

/**
 * Calculates the total quantity of all farms in a given list.
 *
 * This function adds up the quantity of each farm in the list. If the list is empty or not provided,
 * it returns 0.
 *
 * @param {FreezerItem[]} farms - The list of farms whose quantities are to be summed up.
 *
 * @returns {number} The total quantity of all farms in the given list. If the list is empty or not provided,
 * the function returns 0.
 */
export const getTotalFarmsQuantity = (farms?: FreezerItem[]) => {
  if (!farms?.length) return 0;
  return farms.reduce((count, farm) => count + Number(farm.quantity), 0);
};

/**
 * Returns the closest or furthest time between two time values based on the given preference.
 *
 * @param {"closest" | "furthest"} preference - Preference for the comparison. If it's "closest", the function will return the closest time to the current time, otherwise the furthest.
 * @param {string} [firstTime] - The first time value.
 * @param {string} [secondTime] - The second time value.
 * @returns {string | undefined} The time value that is closest or furthest to the current time, based on the preference. If either time is not provided, the function returns the other time value.
 */
const getComparativeTime = (
  preference: "closest" | "furthest",
  firstTime?: string,
  secondTime?: string
) => {
  if (!firstTime) return secondTime;
  if (!secondTime) return firstTime;
  const condition =
    preference === "closest"
      ? new Date(firstTime) > new Date(secondTime)
      : new Date(firstTime) < new Date(secondTime);
  return condition ? secondTime : firstTime;
};

/**
 * Calculates the total earnings for each asset.
 *
 * @param earnings - Array of earnings data.
 * @param assetMap - Map of assetId to asset.
 * @returns Array of data including asset and total earnings.
 */
export function calculateEarnings(
  earnings: Earning[] = [],
  assetMap: Map<string, IMappedAsset<IAssetMetaData>>,
  estimates: Earning[] = []
) {
  const result: IYieldFarmingTotalEarnings[] = [];

  const filteredEarnings = earnings.filter(
    ({ status }) => ![EarningStatus.Claimed, EarningStatus.Expired].includes(status)
  );

  for (const earning of filteredEarnings) {
    for (const value of earning.value) {
      const matchingAsset = assetMap.get(value.assetID);
      const estimate = estimates.find((e) => e?.program?.id === earning.program.id);

      if (matchingAsset) {
        const estimateAmount = estimate?.value?.reduce((acc, val) => {
          const amount = new AssetAmount(val.amount, matchingAsset.metadata);
          return acc.add(amount);
        }, new AssetAmount(0, matchingAsset.metadata));

        const existingAsset = result.find((e) => e.asset.metadata.assetId === value.assetID);
        const totalEarnings = new AssetAmount(value.amount, matchingAsset.metadata);

        if (existingAsset) {
          existingAsset.totalEarnings = existingAsset.totalEarnings.add(totalEarnings);
          existingAsset.expiration = getComparativeTime(
            "closest",
            existingAsset.expiration,
            earning.expiration?.format
          );
          existingAsset.txExpiration = getComparativeTime(
            "furthest",
            existingAsset.txExpiration,
            earning.txExpiration?.format
          );
          existingAsset.txRollbackHorizon = getComparativeTime(
            "furthest",
            existingAsset.txRollbackHorizon,
            earning.txRollbackHorizon?.format
          );
          existingAsset.status.push(earning.status);
        } else {
          result.push({
            asset: matchingAsset,
            estimateAmount,
            expiration: earning.expiration?.format,
            program: earning.program,
            status: [earning.status],
            totalEarnings,
            txExpiration: earning.expiration?.format,
            txRollbackHorizon: earning.txRollbackHorizon?.format,
          });
        }
      }
    }
  }

  const extendedResult = result.map((item) => {
    const someEarningsCooldown = item.status.some((status) => status === EarningStatus.Cooldown);
    const someEarningsPending = item.status.some((status) => status === EarningStatus.Pending);
    const someEarningsUnverified = item.status.some(
      (status) => status === EarningStatus.Unverified
    );

    return { ...item, someEarningsCooldown, someEarningsUnverified, someEarningsPending };
  });

  for (const estimate of estimates) {
    const existingEstimate = extendedResult.find(
      ({ program }) => program.id === estimate.program.id
    );
    if (!existingEstimate) {
      const asset = assetMap.get(estimate.value[0].assetID);
      if (asset) {
        const estimateAmount = estimate?.value?.reduce((acc, val) => {
          const amount = new AssetAmount(val.amount, asset.metadata);
          return acc.add(amount);
        }, new AssetAmount(0, asset.metadata));
        extendedResult.push({
          asset,
          estimateAmount,
          expiration: estimate.expiration?.format,
          program: estimate.program,
          someEarningsCooldown: false,
          someEarningsPending: false,
          someEarningsUnverified: false,
          status: [estimate.status],
          totalEarnings: new AssetAmount(0, asset.metadata),
          txExpiration: estimate.expiration?.format,
          txRollbackHorizon: estimate.txRollbackHorizon?.format,
        });
      }
    }
  }

  return extendedResult;
}

/**
 * This utility function extracts all the `lpAsset` values from the `delegation` and `value` arrays in each Position.
 * It also filters out any empty or duplicate strings.
 * This is useful i.e. to fetch related pools for each LP asset.
 *
 * @param {Position[]} positions - An array of Position objects.
 * @returns {string[]} - An array of unique, non-empty lpAsset strings.
 */
export const extractLpAssetsFromPositions = (positions?: Position[]): string[] => {
  if (!positions?.length) return [];

  // First flatMap over the positions and delegations to extract the lpAsset strings
  const lpAssets = positions.flatMap((position) => {
    return [
      ...position.delegation.reduce((arr: string[], delegation) => {
        if (delegation.pool?.lpAsset) arr.push(delegation.pool.lpAsset);
        return arr;
      }, []),
      ...position.value.map((value) => value.assetID),
    ];
  });

  // Then filter out any empty strings
  const onlyLpAssets = lpAssets.filter((asset) => isLPAsset(asset));

  // Finally, convert the array to a Set and back to an array to remove duplicates
  const uniqueLpAssets = Array.from(new Set(onlyLpAssets));

  return uniqueLpAssets;
};

export const mergePositions = (
  positions?: Position[],
  programs?: Program[],
  poolMap?: TAssetIdPoolMap
) => {
  if (!positions || !programs || !poolMap) {
    return;
  }

  // Create a map of programs, so we can look them up by ID later
  const programsById: { [id: string]: Program } = {};
  for (const p of programs) {
    programsById[p.id] = p;
  }

  // Compute the total *voting power* (i.e. staked asset ownership) for each program, and each position
  const totalStakedAssetByProgram: { [id: string]: bigint } = {};
  const stakedAssetByPosition: { [key: string]: { [programId: string]: bigint } } = {};

  for (const position of positions) {
    const positionKey = `${position.txHash}#${position.index}`;
    stakedAssetByPosition[positionKey] = {};

    for (const program of programs) {
      // If the program doesn't have a staked asset, there is no voting power, so just ignore it
      if (!program?.stakedAsset && program?.stakedAsset !== "") continue;

      // Sum up both *owned* stakedAsset for this program
      // and the equivalent value of any LP tokens
      let totalStakedAssetInPosition = 0n;
      for (const v of position.value) {
        if (v.assetID === program.stakedAsset) {
          totalStakedAssetInPosition += BigInt(v.amount);
        } else if (poolMap.has(v.assetID)) {
          const pool = poolMap.get(v.assetID);
          const [depositedAssetA, depositedAssetB] = getTokensForLp(
            BigInt(v.amount),
            BigInt(pool?.quantityA || 0),
            BigInt(pool?.quantityB || 0),
            BigInt(pool?.quantityLP || 0)
          );
          if (pool?.assetA.assetId === program.stakedAsset) {
            totalStakedAssetInPosition += depositedAssetA;
          } else if (pool?.assetB.assetId === program.stakedAsset) {
            totalStakedAssetInPosition += depositedAssetB;
          }
        }
      }
      // Record the total staked asset (voting power) for both the position specifically, and globally
      // so that we can compute percentages
      stakedAssetByPosition[positionKey][program.id] = totalStakedAssetInPosition;
      totalStakedAssetByProgram[program.id] =
        (totalStakedAssetByProgram[program.id] ?? 0n) + totalStakedAssetInPosition;
    }
  }

  // Sum up the asset amounts, so we can present one big value
  const newMultiAsset: { [assetID: string]: bigint } = {};
  for (const position of positions) {
    for (const v of position.value) {
      newMultiAsset[v.assetID] = (newMultiAsset[v.assetID] ?? 0n) + BigInt(v.amount);
    }
  }

  // And construct a "merged position", which represents the single-position abstraction
  // that we present to the user
  const newPosition: IMergedPosition = {
    delegation: [],
    inputs: [],
    value: [],
  };

  // for each of the assets we summed up, push a record into `value`
  for (const [assetID, amount] of Object.entries(newMultiAsset)) {
    newPosition.value.push({ assetID, amount: amount.toString() });
  }

  // We'll keep track of the pools by ident, so we can construct a delegation at the end
  const poolsById: { [poolIdent: string]: Pool } = {};

  // Now for the tricky part;
  // we need to merge the delegations, respecting not just their weights
  // but the size of the voting power in each position: if a position has "10" voting power
  // and assigns a weight of 1,000,000 to a pool, it has a small effect compared to
  // a position with 1,000,000 voting power that assigns a weight of 10 to the pool
  const delegations: { [programId: string]: { [poolIdent: string]: number } } = {};
  const totalWeightByProgram: { [programId: string]: number } = {};

  // 1,000 RBERRY, 00: weight of 1

  // 10 RBERRY, 01: weight of 1000

  // 1,010 RBERRY, 00: weight of 99, 01: weight of 1
  for (const position of positions) {
    const positionKey = `${position.txHash}#${position.index}`;
    newPosition.inputs.push({ txHash: position.txHash, index: position.index });

    let totalPositionWeightByProgram: { [programId: string]: number } = {};
    for (const delegation of position.delegation) {
      // Filter out invalid delegation datums.
      if (!delegation?.pool || !delegation?.program) {
        continue;
      }

      const existing = totalPositionWeightByProgram[delegation.program.id] ?? 0;
      totalPositionWeightByProgram[delegation.program.id] = existing + delegation.weight;
    }

    for (const delegation of position.delegation) {
      if (
        !delegation?.program ||
        !delegation?.pool ||
        (!programsById[delegation.program.id]?.stakedAsset &&
          programsById[delegation.program.id]?.stakedAsset !== "")
      ) {
        continue;
      }

      const program = programsById[delegation.program.id];

      // We want to calculate a new estimate for the delegation weight
      // this is the percentage of the *stakedAsset* in this position,
      // times the percentage of *weight* in this pool
      // to minimize arithmetic error, we compute numerator and denominator
      const numerator = stakedAssetByPosition[positionKey][program.id] * BigInt(delegation.weight);
      const denominator =
        totalStakedAssetByProgram[program.id] * BigInt(totalPositionWeightByProgram[program.id]);
      // Calculate a decent weight, using the integer percentage
      // this won't be exact, but it'll give nice human readable numbers that can be edited as percentages
      const weight = numerator === 0n ? 0 : Number(((100n * numerator) / denominator).toString());

      // Store the total weight, so we can calculate the amounts for each row (maybe not needed)
      totalWeightByProgram[program.id] = (totalWeightByProgram[program.id] ?? 0) + weight;
      // Record this delegation weight for the program/pool
      delegations[program.id] = delegations[program.id] ?? {};
      poolsById[delegation.pool.poolIdent] = delegation.pool;
      delegations[program.id][delegation.pool.poolIdent] =
        (delegations[program.id][delegation.pool.poolIdent] ?? 0) + weight;
    }
  }
  // Flatten out the { program: { pool: weight } } map into a list of MergedDelegation's
  for (const [programId, programDelegations] of Object.entries(delegations)) {
    for (const [poolIdent, weight] of Object.entries(programDelegations)) {
      // Compute the amount of voting weight allocated to this option, if there is any voting power allocated to it
      // note: might not be neccesary
      const weightedAmount =
        totalWeightByProgram[programId] === 0
          ? 0n
          : (totalStakedAssetByProgram[programId] * BigInt(weight)) /
            BigInt(totalWeightByProgram[programId]);

      newPosition.delegation.push({
        program: programsById[programId],
        pool: poolsById[poolIdent],
        weight,
        amount: weightedAmount,
      });
    }
  }
  return newPosition;
};

const getPositionValues = (
  val: Value,
  assetMap: Map<string, IMappedAsset<IAssetMetaData>>,
  lpAssetPoolMap: TAssetIdPoolMap,
  adaInUsd?: number
): IEnhancedValue | undefined => {
  const pool = lpAssetPoolMap.get(val.assetID);
  const asset = assetMap.get(val.assetID);
  if (!asset) return undefined;
  const assetAmount = new AssetAmount(val.amount, asset.metadata);
  const { totalAmountInADA, totalAmountInUSD } = calculateAssetValueInADAAndUSD(
    { ...asset, quantity: assetAmount },
    pool,
    adaInUsd
  );
  const totalAmount = isAdaAssetId(asset.metadata.assetId)
    ? asset.quantity.amount
    : asset.quantity.amount + assetAmount.amount;
  const totalAssetAmount = new AssetAmount(totalAmount, asset.metadata);
  const percentage = assetAmount.value.divide(totalAssetAmount.value).toNumber();
  const percentageLocked =
    percentage > 1 ? null : assetAmount.value.divide(totalAssetAmount.value).toNumber(); // checking if percentage is greater than 1 because of utxo mismatch after tx submission

  return {
    ...val,
    asset,
    assetAmount,
    percentageLocked,
    pool,
    totalAssetAmount,
    // Somehow it's assuming a third type buy that's not true.
    totalAmountInADA: totalAmountInADA as AssetAmount<IAssetMetaData> | undefined,
    totalAmountInUSD,
  };
};

/**
 * Gets the estimation matching the provided pool identifier.
 *
 * This function traverses the list of estimations, and for each estimation:
 * - It returns undefined if the pool is disqualified,
 * - It returns the estimation data if no eligible pools are defined,
 * - If eligible pools are defined, it returns the estimation data for matching pools,
 * - In all other cases, it returns undefined.
 *
 * @param {ReturnType<typeof useYieldFarmingProgramsEstimation>["queries"]} estimations - The estimations to search through.
 * @param {string} ident - The identifier of the pool to match.
 * @returns {object|undefined} The matching estimation data or undefined.
 */
export const getMatchingEstimationByPool = (
  estimations: ReturnType<typeof useYieldFarmingProgramsEstimation>["queries"],
  ident: string
) => {
  const filteredEstimations: CalculationStats[] = [];
  estimations?.forEach(({ data }) => {
    if (data?.program?.disqualifiedPools?.map(({ poolIdent }) => poolIdent)?.includes(ident)) {
      return;
    }

    // If there is no `eligiblePools` property, that means all are eligible.
    if (
      !data?.program?.eligiblePools ||
      data?.program?.eligiblePools?.map(({ poolIdent }) => poolIdent)?.includes(ident)
    ) {
      filteredEstimations.push(data as CalculationStats);
    }

    return false;
  });

  return filteredEstimations;
};

/**
 * This function enhances the provided positionValues by including additional information from assetMap and lpAssetPoolMap.
 * @param positionValues - An array of position values.
 * @param assetMap - A map of asset information.
 * @param lpAssetPoolMap - A map of LP asset pools.
 * @param adaInUsd - Current Price of ADA in USD. Used to calculate the value of LPs in ADA.
 * @returns An array of enhanced position values.
 */
export const enhanceMergedPositionValues = (
  positionValues?: Value[],
  assetMap?: Map<string, IMappedAsset<IAssetMetaData>>,
  lpAssetPoolMap?: TAssetIdPoolMap,
  adaInUsd?: number
): IEnhancedValue[] | undefined => {
  if (!positionValues || !assetMap || !lpAssetPoolMap) return;

  const adaPosition = positionValues.find(({ assetID }) => assetID === ADA_METADATA.assetId);
  const restPositions = positionValues.filter(({ assetID }) => assetID !== ADA_METADATA.assetId);

  const data = orderBy(
    restPositions.reduce((arr: IEnhancedValue[], val) => {
      const positionValues = getPositionValues(val, assetMap, lpAssetPoolMap, adaInUsd);
      if (positionValues) arr.push(positionValues);
      return arr;
    }, []),
    "percentageLocked",
    "desc"
  );

  if (adaPosition) {
    const adaPositionValues = getPositionValues(adaPosition, assetMap, lpAssetPoolMap, adaInUsd);
    if (adaPositionValues) data.unshift(adaPositionValues);
  }

  return data;
};

/**
 * This function enhances the provided positionDelegations by including additional information from poolMap and stakedAssets.
 * @param positionDelegations - An array of position delegations.
 * @param poolMap - A map of pools.
 * @param stakedAssets - An array of staked assets.
 * @returns An array of enhanced delegations.
 */
export const enhanceMergedPositionDelegations = (
  positionDelegations?: Delegation[],
  poolMap?: TAssetIdPoolMap,
  stakedAssets?: Asset[]
): IEnhancedDelegation[] | undefined => {
  if (!positionDelegations || !poolMap || !stakedAssets) return;

  return orderBy(
    positionDelegations.reduce((arr: IEnhancedDelegation[], delegation) => {
      if (delegation.pool) {
        const pool = poolMap.get(delegation.pool.lpAsset);
        arr.push({ ...delegation, pool });
      }
      return arr;
    }, []),
    "program.label",
    "asc"
  );
};

/**
 * This function merges all undelegated delegations into one.
 * @param delegation - An array of enhanced delegations.
 * @returns An array of delegations where all undelegated delegations have been merged.
 */
export const mergeAllUndelegated = (delegation: IEnhancedDelegation[]) =>
  delegation.reduce((delegation, current) => {
    // Check if pool is undefined
    if (current.pool === undefined) {
      // If the accumulator already has an undefined pool object, add to it
      const existing = delegation.find((obj) => obj.pool === undefined);
      if (existing) {
        if (existing.amount && current.amount) existing.amount += current.amount;
        existing.weight += current.weight;
      } else {
        // If the accumulator doesn't have an undefined pool object, create it
        delegation.push({
          amount: current.amount,
          pool: undefined,
          weight: current.weight,
          program: current.program,
        });
      }
    } else {
      // If pool is not undefined, just add the object to the accumulator
      delegation.push(current);
    }
    return delegation;
  }, [] as IEnhancedDelegation[]);

/**
 * This function groups delegations based on their program id.
 * @param mergedPosition - The merged position of delegations.
 * @param stakedAssets - An array of staked assets.
 * @returns A grouping of delegations based on their program id.
 */
export const getGroupedDelegation = (
  mergedPosition: IEnhancedMergedPosition,
  stakedAssets: IMappedAsset<IAssetMetaData>[]
): TGroupedDelegation => {
  return mergedPosition?.delegation?.reduce((groupedDelegation, delegation) => {
    const programId = delegation.program?.id;
    if (programId && !groupedDelegation[programId]) {
      groupedDelegation[programId] = {
        delegation: [],
        programId: undefined,
        programLabel: undefined,
        stakedAsset: undefined,
        totalAmountStaked: 0n,
        totalWeight: 0,
      };
    }

    const stakedAsset = stakedAssets?.find(
      (asset) => delegation.program?.stakedAsset === asset.metadata.assetId
    )?.metadata;

    if (programId) {
      groupedDelegation[programId].delegation.push(delegation);
      groupedDelegation[programId].programId = delegation.program?.id;
      groupedDelegation[programId].programLabel = delegation.program?.label;
      groupedDelegation[programId].stakedAsset = stakedAsset;
      groupedDelegation[programId].totalAmountStaked += delegation.amount || 0n;
      groupedDelegation[programId].totalWeight += delegation.weight;

      groupedDelegation[programId].delegation = mergeAllUndelegated(
        groupedDelegation[programId].delegation
        // Sort this based on the weight of the delegation (the higher the weight, the higher the priority)
      ).sort((a, b) => (a.weight > b.weight ? -1 : 1));
    }

    return groupedDelegation;
  }, {} as TGroupedDelegation);
};

/**
 * This function creates an empty grouping of delegations based on the provided programs and stakedAssets.
 * @param programs - An array of programs.
 * @param stakedAssets - An array of staked assets.
 * @returns A grouping of empty delegations based on the provided programs and stakedAssets.
 */
export const getGroupedEmptyDelegation = (
  programs: Program[],
  stakedAssets: IMappedAsset<IAssetMetaData>[],
  lpPoolMap: TAssetIdPoolMap
): TGroupedDelegation => {
  return programs.reduce((groupedDelegation, program) => {
    const programId = program.id;
    const stakedAsset = stakedAssets?.find((asset) => {
      const pool = lpPoolMap.get(asset.metadata.assetId);
      return (
        program.stakedAsset === asset.metadata.assetId ||
        (pool &&
          program.stakedAsset &&
          [pool.assetA.assetId, pool.assetB.assetId].includes(program.stakedAsset))
      );
    })?.metadata;

    // Only add a program to the groupedDelegation if the staked asset is found in the stakedAssets array
    if (stakedAsset) {
      groupedDelegation[programId] = {
        delegation: [],
        programId: undefined,
        programLabel: undefined,
        stakedAsset: undefined,
        totalAmountStaked: 0n,
        totalWeight: 100,
      };

      groupedDelegation[programId].programId = program.id;
      groupedDelegation[programId].programLabel = program.label;
      groupedDelegation[programId].stakedAsset = stakedAsset;
      groupedDelegation[programId].delegation = [
        {
          weight: 100,
          program,
          pool: undefined,
          amount: 0n,
        },
      ];
    }

    return groupedDelegation;
  }, {} as TGroupedDelegation);
};

/**
 * This function adds an undelegated item to each delegation in groupedDelegation if all the delegations within a group are pool-based.
 * @param groupedDelegation - A grouping of delegations.
 * @param programs - An array of programs.
 * @returns A grouping of delegations where undelegated items have been added where necessary.
 */
export const addUndelegatedItemToDelegation = (
  groupedDelegation: TGroupedDelegation,
  programs: Program[]
) => {
  if (!groupedDelegation || !programs) return;

  return Object.keys(groupedDelegation).reduce((acc, programId) => {
    const undelegatedItem: IEnhancedDelegation = {
      pool: undefined,
      amount: 0n,
      weight: 0,
      program: programs.find((program) => program.id === programId),
    };
    const isEveryDelegationPoolBased = groupedDelegation[programId].delegation.every(
      (delegation) => delegation.pool
    );

    if (isEveryDelegationPoolBased) {
      return {
        ...acc,
        [programId]: {
          ...groupedDelegation[programId],
          delegation: [...groupedDelegation[programId].delegation, undelegatedItem],
        },
      };
    }

    return {
      ...acc,
      [programId]: groupedDelegation[programId],
    };
  }, {} as TGroupedDelegation);
};
